/**
 * Filtered-playback segments: turn the moments the filters currently show
 * into an ordered, merged list of frame ranges, so the video can play only
 * what matches and skip everything between (no pause — just a seek to the
 * next matching range).
 *
 * Pure module, no React: buildPlaybackSegments feeds the page's memo, and
 * findSkipTarget answers the per-tick question inside VideoPlayer's
 * timeupdate handler.
 */
import type { Moment } from './types'

export interface PlaybackSegment {
  startFrame: number
  endFrame: number
}

export interface SegmentOptions {
  fps: number
  /** Lead-in before a point event (a pass has one frame; you want the build-up). */
  prePadSec?: number
  /** Tail after a point event. */
  postPadSec?: number
  /** Two segments closer than this play through as one — a skip this short is a stutter. */
  mergeGapSec?: number
  /** Restrict to a frame window (a half, a section). Segments are clipped, not dropped whole. */
  range?: { startFrame: number; endFrame: number } | null
}

const PRE_PAD_SEC = 3
const POST_PAD_SEC = 5
const MERGE_GAP_SEC = 2

/**
 * Span moments (possession, pressing — start_frame + a later end_frame) keep
 * their exact range: they are already complete phrases, and padding one
 * would bleed into the opponent's possession the filter excluded. Point
 * moments (a pass, a run — one frame) get a watchable window around it.
 */
export function buildPlaybackSegments(
  moments: Moment[],
  opts: SegmentOptions,
): PlaybackSegment[] {
  const fps = opts.fps
  const prePad = Math.round((opts.prePadSec ?? PRE_PAD_SEC) * fps)
  const postPad = Math.round((opts.postPadSec ?? POST_PAD_SEC) * fps)
  const mergeGap = Math.round((opts.mergeGapSec ?? MERGE_GAP_SEC) * fps)

  const raw: PlaybackSegment[] = []
  for (const m of moments) {
    const start = m.start_frame ?? m.frame
    if (start == null) continue
    const end = m.end_frame
    if (end != null && end > start) {
      raw.push({ startFrame: Math.max(0, start), endFrame: end })
    } else {
      raw.push({ startFrame: Math.max(0, start - prePad), endFrame: start + postPad })
    }
  }

  // Clip to the requested window (a half). Clipped, not dropped whole: a
  // possession straddling halftime still shows its first-half part.
  let clipped = raw
  if (opts.range) {
    const { startFrame: lo, endFrame: hi } = opts.range
    clipped = raw
      .filter((s) => s.endFrame > lo && s.startFrame < hi)
      .map((s) => ({
        startFrame: Math.max(s.startFrame, lo),
        endFrame: Math.min(s.endFrame, hi),
      }))
  }

  clipped.sort((a, b) => a.startFrame - b.startFrame || a.endFrame - b.endFrame)

  const merged: PlaybackSegment[] = []
  for (const seg of clipped) {
    const last = merged[merged.length - 1]
    if (last && seg.startFrame <= last.endFrame + mergeGap) {
      if (seg.endFrame > last.endFrame) last.endFrame = seg.endFrame
    } else {
      merged.push({ ...seg })
    }
  }
  return merged
}

/**
 * Where should playback go from `frame`?
 * - null: inside a segment (within tolerance) — keep playing.
 * - a number: in a gap — seek to that frame (the next segment's start).
 * - 'end': past the last segment — nothing left to show.
 *
 * The tolerance absorbs seek-landing error: frameToVideoTime/videoTimeToFrame
 * round differently (floor vs round), so a seek to a segment's start can land
 * a frame or two shy and must not re-trigger the same seek forever. Gaps
 * shorter than the merge threshold no longer exist, so a half-second
 * tolerance can never swallow a real gap.
 */
export function findSkipTarget(
  segments: PlaybackSegment[],
  frame: number,
  toleranceFrames: number,
): number | 'end' | null {
  for (const seg of segments) {
    if (frame <= seg.endFrame) {
      if (frame >= seg.startFrame - toleranceFrames) return null // inside
      return seg.startFrame // in the gap before this segment
    }
  }
  return 'end'
}

/**
 * The clip after `frame` — its start, or null when nothing is ahead. The
 * tolerance treats a landing just shy of a segment start as already there,
 * so "next" pressed right after a skip advances instead of re-seeking the
 * same clip.
 */
export function nextSegmentStart(
  segments: PlaybackSegment[],
  frame: number,
  toleranceFrames: number,
): number | null {
  for (const seg of segments) {
    if (seg.startFrame > frame + toleranceFrames) return seg.startFrame
  }
  return null
}

/**
 * The clip before `frame`. Well into a clip (past restartThresholdFrames)
 * means restart that clip — the music-player convention — otherwise the
 * previous clip's start. In a gap, the start of the clip just passed. Null
 * when nothing is behind.
 */
export function prevSegmentStart(
  segments: PlaybackSegment[],
  frame: number,
  toleranceFrames: number,
  restartThresholdFrames: number,
): number | null {
  let prevStart: number | null = null
  for (const seg of segments) {
    const inside = frame >= seg.startFrame - toleranceFrames && frame <= seg.endFrame
    if (inside) {
      return frame - seg.startFrame > restartThresholdFrames ? seg.startFrame : prevStart
    }
    if (seg.startFrame - toleranceFrames > frame) break
    prevStart = seg.startFrame
  }
  return prevStart
}

/** Total playing time of the segments, for the toolbar label. */
export function segmentsTotalSeconds(segments: PlaybackSegment[], fps: number): number {
  let frames = 0
  for (const s of segments) frames += s.endFrame - s.startFrame
  return frames / fps
}
